import styled from 'styled-components';
import { updateObjectNestedProperties } from '../../../functions/launcherElement/updateObjectNestedProperties';
import { LauncherWindowData, useLauncherWindowStore } from '../../../hooks/stores/launcherWindowStore';
import { DropdownInput } from '../../common/baseInputs/DropdownInput';
import { NumberInput } from '../../common/baseInputs/NumberInput';
import { PositioningInput } from '../../common/specificPropInputs/containerProps/PositioningInput';
import { BackgroundInput } from '../../common/specificPropInputs/styleProps/containerComboInput/BackgroundInput';
import { TransitionInput } from '../../common/specificPropInputs/styleProps/effectComboInput/TransitionInput';
import { TextColorInput } from '../../common/specificPropInputs/styleProps/textComboInput/TextColorInput';
import { useScene } from '../../../context/sceneContext';

export const WindowDrawer = () => {
  const scene = useScene();
  const [launcherWindowData, setLauncherWindowData] = useLauncherWindowStore<LauncherWindowData>();

  const updateValue = (key: string, value?: string | number) => {
    if (key === 'width') {
      setLauncherWindowData(
        updateObjectNestedProperties(launcherWindowData, { windowWidth: value })
      );
      return;
    }
    if (key === 'height') {
      setLauncherWindowData(
        updateObjectNestedProperties(launcherWindowData, {
          windowHeight: value,
        })
      );
      return;
    }
    setLauncherWindowData(
      updateObjectNestedProperties(
        launcherWindowData,
        { [key]: value },
        'class'
      )
    );
  };

  const data = {
    ...(launcherWindowData.class as any),
    width: launcherWindowData.windowWidth,
    height: launcherWindowData.windowHeight,
  };

  const computedData = scene.getElementComputedStyle('launcherWindow');
  const computedTransitionString = computedData
    ? `all ${computedData.transitionDuration} ${computedData.transitionTimingFunction}`
    : undefined;

  return (
    <ScrollContainer className="scroll">
      <Container>
        <Row>
          <NumberInput
            label="W"
            value={data.width}
            onChange={(newValue) => updateValue('width', newValue)}
            placeholder="auto"
            precision={0}
            minValue={0}
          />
          <NumberInput
            label="H"
            value={data.height}
            onChange={(newValue) => updateValue('height', newValue)}
            placeholder="auto"
            precision={0}
            minValue={0}
          />
        </Row>

        <Separator />

        <Row>
          <BackgroundInput
            value={data.background}
            onChange={(newValue) => updateValue('background', newValue)}
          />
        </Row>
        <Row>
          <PositioningInput forWindow />
        </Row>
        <Row>
          <NumberInput
            iconClassName="icon-padding"
            id={'window-padding-input'}
            tooltip="Padding"
            value={data.padding || 0}
            onChange={(newValue) => updateValue('padding', newValue)}
            precision={0}
            minValue={0}
            defaultUnit="px"
            inputStyle={{ textAlign: 'center' }}
          />
          <NumberInput
            iconClassName="icon-gap"
            id={'window-gap-input'}
            tooltip="Gap"
            value={data.gap || 0}
            onChange={(newValue) => updateValue('gap', newValue)}
            precision={0}
            minValue={0}
            defaultUnit="px"
            inputStyle={{ textAlign: 'center' }}
          />
          <NumberInput
            iconClassName="icon-radius"
            id={'window-radius-input'}
            tooltip="Corner Radius"
            value={data.borderRadius || 0}
            onChange={(newValue) => updateValue('borderRadius', newValue)}
            precision={0}
            minValue={0}
            defaultUnit="px"
            inputStyle={{ textAlign: 'center' }}
          />
        </Row>

        <Label>
          <Separator />
          Text
          <Separator />
        </Label>

        <Row>
          <DropdownInput
            iconClassName="icon-text-font-family"
            tooltip="Font Family"
            id="window-font-family-input"
            value={data.fontFamily}
            onChange={(newValue) => updateValue('fontFamily', String(newValue))}
            options={[
              ...launcherWindowData.fonts?.map((font) => ({
                value: font.fontFamily,
                label: font.fontFamily,
              })),
            ]}
          />
        </Row>
        <Row>
          <Row>
            <NumberInput
              iconClassName="icon-text-size"
              id="window-input-text-size"
              tooltip="Font Size"
              value={data.fontSize}
              onChange={(newValue) => updateValue('fontSize', newValue)}
              precision={0}
              minValue={4}
              defaultUnit="px"
            />
          </Row>
          <Row>
            <DropdownInput
              iconClassName="icon-text-weight"
              id="window-input-font-weight"
              tooltip="Font Weight"
              value={data.fontWeight}
              onChange={(newValue) =>
                updateValue('fontWeight', Number(newValue))
              }
              options={[
                { value: 100, label: '100 - Thin', shortLabel: '100' },
                { value: 200, label: '200 - Light', shortLabel: '200' },
                { value: 300, label: '300 - Book', shortLabel: '300' },
                { value: 400, label: '400 - Regular', shortLabel: '400' },
                { value: 500, label: '500 - Medium', shortLabel: '500' },
                { value: 600, label: '600 - Semi-Bold', shortLabel: '600' },
                { value: 700, label: '700 - Bold', shortLabel: '700' },
                { value: 800, label: '800 - Extra-Bold', shortLabel: '800' },
                { value: 900, label: '900 - Heavy', shortLabel: '900' },
              ]}
              horizontalAlign="right"
            />
          </Row>
        </Row>
        <Row>
          <Row>
            <NumberInput
              iconClassName="icon-text-line-height"
              id="window-input-line-height"
              tooltip="Line Height"
              value={data.lineHeight}
              onChange={(newValue) => updateValue('lineHeight', newValue)}
              defaultUnit="px"
              minValue={0}
            />
          </Row>
          <Row>
            <NumberInput
              iconClassName="icon-text-letter-spacing"
              id="window-input-letter-spacing"
              tooltip="Letter Spacing"
              value={data.letterSpacing}
              onChange={(newValue) => updateValue('letterSpacing', newValue)}
              defaultUnit="px"
              minValue={0}
            />
          </Row>
        </Row>
        <Row>
          <TextColorInput
            value={data.color}
            onChange={(newValue) => updateValue('color', newValue)}
          />
        </Row>

        <Label>
          <Separator />
          Scrollbar
          <Separator />
        </Label>

        <Row>
          <NumberInput
            label="Size"
            value={data['::-webkit-scrollbar']?.width}
            onChange={(newValue) =>
              setLauncherWindowData(
                updateObjectNestedProperties(
                  launcherWindowData,
                  {
                    ['::-webkit-scrollbar.width']: newValue,
                    ['::-webkit-scrollbar.height']: newValue,
                  },
                  'class'
                )
              )
            }
            defaultUnit="px"
            minValue={0}
            inputStyle={{ textAlign: 'center' }}
          />
          <NumberInput
            iconClassName="icon-radius"
            id={'window-scrollbar-radius-input'}
            tooltip="Scrollbar Radius"
            value={data['::-webkit-scrollbar-track']?.borderRadius}
            onChange={(newValue) =>
              setLauncherWindowData(
                updateObjectNestedProperties(
                  launcherWindowData,
                  {
                    ['::-webkit-scrollbar-track.borderRadius']: newValue,
                    ['::-webkit-scrollbar-thumb.borderRadius']: newValue,
                  },
                  'class'
                )
              )
            }
            defaultUnit="px"
            minValue={0}
            inputStyle={{ textAlign: 'center' }}
          />
        </Row>
        <Row>
          <Label style={{ paddingLeft: '8px' }}>Track</Label>
        </Row>
        <Row>
          <BackgroundInput
            value={data['::-webkit-scrollbar-track']?.backgroundColor}
            onChange={(newValue) =>
              setLauncherWindowData(
                updateObjectNestedProperties(
                  launcherWindowData,
                  {
                    ['::-webkit-scrollbar-track.backgroundColor']: newValue,
                  },
                  'class'
                )
              )
            }
            onlySolid
          />
        </Row>
        <Row>
          <Label style={{ paddingLeft: '8px' }}>Thumb</Label>
        </Row>
        <Row>
          <BackgroundInput
            value={data['::-webkit-scrollbar-thumb']?.backgroundColor}
            onChange={(newValue) =>
              setLauncherWindowData(
                updateObjectNestedProperties(
                  launcherWindowData,
                  { ['::-webkit-scrollbar-thumb.backgroundColor']: newValue },
                  'class'
                )
              )
            }
            onlySolid
          />
        </Row>

        <Label>
          <Separator />
          Effect
          <Separator />
        </Label>

        <Row>
          <TransitionInput
            value={data.transition}
            onChange={(newValue) =>
              setLauncherWindowData(
                updateObjectNestedProperties(
                  launcherWindowData,
                  { transition: newValue },
                  'class.*'
                )
              )
            }
          />
        </Row>
      </Container>
    </ScrollContainer>
  );
};

const ScrollContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  overflow-y: auto;
  height: calc(100vh - 60px);
`;

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: var(--padding-1);

  display: flex;
  flex-direction: column;

  gap: 5px;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  margin: 5px 0;

  background-color: var(--neutral-3);
`;

const Row = styled.div`
  width: 100%;
  display: flex;

  gap: 5px;
`;

const Label = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin: 5px 0;
  whiet-space: nowrap;

  font-size: 11px;

  display: flex;
  align-items: center;
  gap: 15px;
`;
